import { useState, useEffect } from 'react';
import { getDeviceState, type DeviceState } from '../utils/deviceDetection';
import {
  LAYOUT_MODE_MOBILE,
  LAYOUT_MODE_DESKTOP,
  INPUT_MODE_TOUCH,
  INPUT_MODE_MOUSE,
  ORIENTATION_PORTRAIT,
  ORIENTATION_LANDSCAPE,
} from '../constants/deviceDetection';

/**
 * Responsive Design Hook (INTERNAL)
 *
 * @internal — use {@link useDeviceLayout} instead. `useResponsive` is the
 * low-level adapter that backs `useDeviceLayout`. Component code MUST go
 * through `useDeviceLayout` (see F-N2 in `.claude/rules/frontend/rules.md`).
 *
 * Provides reactive device state for layout and input mode decisions.
 *
 * Key Concepts:
 * - Layout Mode: Determines UI structure (mobile/desktop) based on screen size + orientation
 * - Input Mode: Determines control type (touch/mouse) based on device capabilities
 *
 * These are SEPARATE concerns:
 * - A tablet in portrait shows MOBILE layout with TOUCH controls
 * - A tablet in landscape shows DESKTOP layout with TOUCH controls
 * - A desktop shows DESKTOP layout with MOUSE controls
 * - A desktop with touchscreen shows DESKTOP layout with TOUCH controls
 */
export const useResponsive = () => {
  const [deviceState, setDeviceState] = useState<DeviceState>(() =>
    getDeviceState()
  );

  useEffect(() => {
    // Handle window resize
    const handleResize = () => {
      setDeviceState(getDeviceState());
    };

    // Handle orientation change (for tablets)
    const handleOrientationChange = () => {
      setDeviceState(getDeviceState());
    };

    // Listen for resize events
    window.addEventListener('resize', handleResize);

    // Listen for orientation change events
    window.addEventListener('orientationchange', handleOrientationChange);

    // Also listen for orientation media query changes (more reliable on some devices)
    const orientationQuery = window.matchMedia('(orientation: portrait)');
    const handleOrientationMediaChange = () => {
      setDeviceState(getDeviceState());
    };

    // Modern browsers support addEventListener on MediaQueryList
    if (orientationQuery.addEventListener) {
      orientationQuery.addEventListener('change', handleOrientationMediaChange);
    } else {
      // Fallback for older browsers
      orientationQuery.addListener(handleOrientationMediaChange);
    }

    return () => {
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('orientationchange', handleOrientationChange);

      if (orientationQuery.removeEventListener) {
        orientationQuery.removeEventListener(
          'change',
          handleOrientationMediaChange
        );
      } else {
        orientationQuery.removeListener(handleOrientationMediaChange);
      }
    };
  }, []);

  return {
    // === Layout Mode (determines UI structure) ===
    /** Current layout mode: 'mobile' or 'desktop' */
    layoutMode: deviceState.layoutMode,
    /** True if using mobile layout (phones or tablets in portrait) */
    isMobileLayout: deviceState.layoutMode === LAYOUT_MODE_MOBILE,
    /** True if using desktop layout (tablets in landscape or desktops) */
    isDesktopLayout: deviceState.layoutMode === LAYOUT_MODE_DESKTOP,

    // === Input Mode (determines control type) ===
    /** Current input mode: 'touch' or 'mouse' */
    inputMode: deviceState.inputMode,
    /** True if device has touch capability */
    isTouchDevice: deviceState.hasTouch,
    /** True if touch is the primary input method */
    isTouchPrimary: deviceState.inputMode === INPUT_MODE_TOUCH,
    /** True if mouse is the primary input method */
    isMousePrimary: deviceState.inputMode === INPUT_MODE_MOUSE,
    /** True if device has hover capability (usually means mouse) */
    hasHover: deviceState.hasHover,

    // === Orientation ===
    /** Current orientation: 'portrait' or 'landscape' */
    orientation: deviceState.orientation,
    /** True if device is in portrait orientation */
    isPortrait: deviceState.orientation === ORIENTATION_PORTRAIT,
    /** True if device is in landscape orientation */
    isLandscape: deviceState.orientation === ORIENTATION_LANDSCAPE,

    // === Screen Dimensions ===
    /** Screen width in pixels */
    width: deviceState.width,
    /** Screen height in pixels */
    height: deviceState.height,

    // === Device Capabilities ===
    /** Primary pointer type: 'coarse' (touch) or 'fine' (mouse) */
    primaryPointer: deviceState.primaryPointer,
  };
};
